import React, {useCallback, useEffect, useMemo} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {defaultCurrencySymbol} from '@/config';
import globalStore from '@/services/global.state';
import {goBack, goTo, toAgentApply, toPriceStr} from '@/utils';
import {BackIcon} from '../svg.variables';
import CommissionContent from './commission-content';
import DepositBonusContent from './deposit-bonus-content';
import {useInvitationHomeStore} from './invitation-home.store';
import InvitationContent from './invitation-content';
import MyDataContent from './my-data-content';
import SubordinateDataContent from './subordinate-data-content';
import styles from './invitation-tabs.module.scss';
import stickySurfaceStyles from './refer-earn-sticky-surface.module.scss';
import {notifyReferEarnInvitationReady} from '../refer-earn-entry.service';
import {referEarnWhiteReferralPageBgImage} from '../white-theme-assets';

interface HeaderProps {
  hideBack: boolean;
  balance: number;
}

interface ReferralTabsProps {
  activeTab: ReferralTabKey;
  onTabChange: (tab: ReferralTabKey) => void;
}

type ReferralTabKey =
  | 'invitation'
  | 'my-data'
  | 'subordinate-data'
  | 'deposit-bonus'
  | 'commission';

function InvitationHeader({hideBack, balance}: HeaderProps) {
  const {t} = useTranslation();

  return (
    <header
      className={[
        stickySurfaceStyles.stickySurface,
        stickySurfaceStyles.pageHeaderSurface,
        'relative z-[2] flex h-11 flex-none items-center justify-between bg-transparent px-2 text-black',
      ].join(' ')}>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {!hideBack && (
          <button
            type="button"
            aria-label="Back"
            onClick={goBack}
            className="flex h-9 w-7 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-black [touch-action:manipulation] active:scale-95">
            <BackIcon width={14} height={14} stroke="currentColor" />
          </button>
        )}
        <h1 className="m-0 min-w-0 truncate text-base font-bold leading-none tracking-[-0.01875rem]">
          {t('invitation.referral.title')}
        </h1>
      </div>
      <ReferEarnBalancePill
        amount={balance || 0}
        rechargeLabel={t('label.recharge')}
        onRecharge={() => goTo('Recharge')}
      />
    </header>
  );
}

function ReferEarnBalancePill({
  amount,
  rechargeLabel,
  onRecharge,
}: {
  amount: number;
  rechargeLabel: string;
  onRecharge: () => void;
}) {
  return (
    <div className="flex h-7 w-[7.5rem] shrink-0 items-center rounded-[0.9375rem] border border-white/25 bg-black/70 px-[0.375rem] py-[0.25rem]">
      <div className="flex min-w-0 flex-1 items-center gap-[0.277344rem]">
        <span className="flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center rounded-full bg-[#F7931A] text-[0.625rem] font-bold leading-none text-white">
          ₹
        </span>
        <span className="min-w-0 truncate font-inter text-[0.625rem] font-extrabold leading-[0.625rem] text-white">
          {defaultCurrencySymbol}
          {toPriceStr(amount, {showCurrency: false})}
        </span>
      </div>
      <button
        type="button"
        aria-label={rechargeLabel}
        onClick={onRecharge}
        className="relative h-5 w-5 shrink-0 appearance-none rounded-full border-0 bg-[#F2C338] p-0 active:scale-[0.94]">
        <span className="absolute left-1/2 top-1/2 h-[0.125rem] w-[0.625rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
        <span className="absolute left-1/2 top-1/2 h-[0.625rem] w-[0.125rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
      </button>
    </div>
  );
}

function ReferralTabs({activeTab, onTabChange}: ReferralTabsProps) {
  const {t} = useTranslation();
  const buttonRefs = React.useRef<
    Partial<Record<ReferralTabKey, HTMLButtonElement>>
  >({});
  const tabs: Array<{
    key: ReferralTabKey;
    label: string;
  }> = [
    {
      key: 'invitation',
      label: t('invitation.referral.tabs.invite'),
    },
    {
      key: 'my-data',
      label: t('invitation.referral.tabs.myData'),
    },
    {
      key: 'subordinate-data',
      label: t('invitation.referral.tabs.subordinate'),
    },
    {
      key: 'deposit-bonus',
      label: t('invitation.referral.tabs.depositBonus'),
    },
    {
      key: 'commission',
      label: t('invitation.referral.tabs.commission'),
    },
  ];

  useEffect(() => {
    buttonRefs.current[activeTab]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeTab]);

  return (
    <div
      className={[
        stickySurfaceStyles.stickySurface,
        stickySurfaceStyles.pageTabsSurface,
        'sticky top-0 z-30 -mx-3 h-[3.0625rem] flex-none bg-transparent',
      ].join(' ')}>
      <nav className="relative z-[2] flex h-full items-center gap-2 overflow-x-auto overflow-y-hidden px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            aria-current={activeTab === tab.key ? 'page' : undefined}
            ref={element => {
              if (element) {
                buttonRefs.current[tab.key] = element;
              }
            }}
            onClick={() => onTabChange(tab.key)}
            className={[
              styles.referralTab,
              'relative flex h-[3.0625rem] min-w-[4.125rem] shrink-0 items-center justify-center border-0 bg-transparent px-[0.875rem] py-0 text-center font-inter text-[0.875rem] font-medium not-italic leading-[1rem] [touch-action:manipulation]',
              activeTab === tab.key
                ? `${styles.referralTabActive} font-bold text-black`
                : 'text-black/85 active:text-black',
            ].join(' ')}>
            <span className="relative z-[1] whitespace-nowrap">
              {tab.label}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
}

const Invitation = () => {
  const hideBack = false;
  const [activeTab, setActiveTab] =
    React.useState<ReferralTabKey>('invitation');
  const code = useInvitationHomeStore(state => state.code);
  const login = useInvitationHomeStore(state => state.login);
  const loading = useInvitationHomeStore(state => state.loading);
  const userInfo = useInvitationHomeStore(state => state.userInfo);
  const agentInfo = useInvitationHomeStore(state => state.agentInfo);
  const userList = useInvitationHomeStore(state => state.userList);
  const setLogin = useInvitationHomeStore(state => state.setLogin);
  const hydrateUser = useInvitationHomeStore(state => state.hydrateUser);
  const loadInvitationData = useInvitationHomeStore(
    state => state.loadInvitationData,
  );
  const doShare = useInvitationHomeStore(state => state.doShare);
  const refreshCode = useInvitationHomeStore(state => state.refreshCode);

  useEffect(() => {
    let paintedFrame = 0;
    const mountedFrame = window.requestAnimationFrame(() => {
      paintedFrame = window.requestAnimationFrame(() => {
        notifyReferEarnInvitationReady();
      });
    });

    return () => {
      window.cancelAnimationFrame(mountedFrame);
      if (paintedFrame) {
        window.cancelAnimationFrame(paintedFrame);
      }
    };
  }, []);

  useEffect(() => {
    setLogin(Boolean(globalStore.token));
    const tokenSub = globalStore.tokenSubject.subscribe(token => {
      setLogin(Boolean(token));
    });
    return () => {
      tokenSub.unsubscribe();
    };
  }, [setLogin]);

  useEffect(() => {
    if (!login) {
      setActiveTab('invitation');
    }
  }, [login]);

  useFocusEffect(
    useCallback(() => {
      hydrateUser();
      if (login) {
        loadInvitationData();
      }
    }, [hydrateUser, loadInvitationData, login]),
  );

  const hasPhone = Boolean(userInfo?.userPhone);
  const completeCount = useMemo(
    () =>
      userList?.filter(item => item.completedNumber >= item.inviteNumber)
        .length || 0,
    [userList],
  );

  const requireLoginAndPhone = useCallback(() => {
    if (!login) {
      goTo('Login');
      return false;
    }

    if (!hasPhone) {
      goTo('BindPhone');
      return false;
    }

    return true;
  }, [hasPhone, login]);

  const handleInvite = useCallback(() => {
    if (requireLoginAndPhone()) {
      doShare();
    }
  }, [doShare, requireLoginAndPhone]);

  const handleRefreshCode = useCallback(() => {
    if (requireLoginAndPhone()) {
      refreshCode();
    }
  }, [refreshCode, requireLoginAndPhone]);

  const handleAgentApply = useCallback(() => {
    if (requireLoginAndPhone()) {
      toAgentApply();
    }
  }, [requireLoginAndPhone]);

  const handleTabChange = useCallback(
    (tab: ReferralTabKey) => {
      if (!login && tab !== 'invitation') {
        setActiveTab('invitation');
        goTo('Login');
        return;
      }

      setActiveTab(tab);

      if (tab === 'invitation') {
        hydrateUser();
        if (login) {
          loadInvitationData();
        }
      }
    },
    [hydrateUser, loadInvitationData, login],
  );

  const renderContent = () => {
    if (activeTab === 'my-data') {
      return (
        <MyDataContent
          login={login}
          agentInfo={agentInfo}
          onTabChange={handleTabChange}
        />
      );
    }

    if (activeTab === 'subordinate-data') {
      return <SubordinateDataContent />;
    }

    if (activeTab === 'deposit-bonus') {
      return <DepositBonusContent />;
    }

    if (activeTab === 'commission') {
      return <CommissionContent />;
    }

    return (
      <InvitationContent
        login={login}
        hasPhone={hasPhone}
        agentInfo={agentInfo}
        userList={userList}
        code={code}
        showAgentApply={
          login && hasPhone && userInfo?.isAgent !== 1 && completeCount >= 3
        }
        onInvite={handleInvite}
        onRefreshCode={handleRefreshCode}
        onAgentApply={handleAgentApply}
      />
    );
  };
  const pageBackgroundStyle = {
    '--refer-earn-page-bg-image': `url(${referEarnWhiteReferralPageBgImage})`,
    backgroundImage: `url(${referEarnWhiteReferralPageBgImage})`,
  } as React.CSSProperties & {'--refer-earn-page-bg-image': string};

  return (
    <main
      aria-busy={loading}
      className="relative h-screen overflow-hidden bg-[#FCFFFA] font-inter text-black">
      <div
        className="relative mx-auto flex h-full w-full max-w-[23.4375rem] flex-col overflow-hidden bg-[#FCFFFA] bg-[length:23.4375rem_auto] bg-top bg-no-repeat"
        style={pageBackgroundStyle}>
        <InvitationHeader
          hideBack={hideBack}
          balance={globalStore.userAmount || 0}
        />
        <div
          data-refer-earn-scroll
          className="relative z-[1] min-h-0 flex-1 overflow-y-auto px-3 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ReferralTabs activeTab={activeTab} onTabChange={handleTabChange} />
          {renderContent()}
        </div>
      </div>
    </main>
  );
};

export default Invitation;
